import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { AppDispatch } from '@/store'
import http from '@/services/http'
import { stockUrl } from '@/services/url'
import { SettingCode } from '@/services/api/settings/type'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { getCustomTagsList, getUserSetting, updateUserSetting } from '@/services/api'
import { ORDER_STATUS_MAP } from '@/containers/Order/Index/constant'

import type * as Types from './types'

const defaultConvertedInfo = {
  /** 是否打开 cod 金额转换的能力 */
  isCodAmountConverted: false,
  /** 是否打开到商品金额的币种转换能力 */
  isGoodsPriceConverted: false,
  /** 转换后的 cod 金额 */
  convertedCodAmount: null,
  /** 页面上的“币种转换”开关的状态是否开启，默认开启 */
  isPageConvertSwitchEnabled: true,
  /** 是否展示页面上的“币种转换”开关 */
  isShowConvertedSwitch: false,
  /** 设置页的“币种转换”开关是否打开 */
  // isConvertSettingEnabled: false,
  /** 是否展示“商品单价”的转换图标 */
  isShowConvertedPriceIcon: false,
  /* 商品币种, 用户更改币种之后，会自动更新 */
  goodsCurrency: null,
  /** 标记场景：商品单价进行了币种转换，但没有汇率或转换结果为 0 */
  isGoodsPriceConvertedException: false,
  /** 标记场景：cod金额进行了币种转换，但没有汇率或转换结果为 0 */
  isCodAmountConvertedException: false,
}
const initialState: Types.IInitialState = {
  expressList: null,
  stocks: null,
  currentPage: 1,
  headerColumns: [],
  headerColumnsInit: [],
  convertedInfo: { ...defaultConvertedInfo },
  searchConditions: null,
  activeCondition: null,
  activeKey: ORDER_STATUS_MAP.PENDING,
  customTags: null,
}

const orderSlice = createSlice({
  name: 'order',
  initialState: initialState,
  reducers: {
    // 物流渠道
    setExpress(state, action: PayloadAction<Types.IExpressType>) {
      return {
        ...state,
        expressList: action.payload,
      }
    },
    setStocks(state, action: PayloadAction<Types.IBatchStock[]>) {
      return {
        ...state,
        stocks: action.payload,
      }
    },

    setCurrentPage(state, action: PayloadAction<number>) {
      return {
        ...state,
        currentPage: action.payload,
      }
    },

    setHeaderColumns(state, action: PayloadAction<Types.IHeaderColumns[]>) {
      return {
        ...state,
        headerColumns: action.payload,
      }
    },

    setHeaderColumnsInit(state, action: PayloadAction<Types.IHeaderColumns[]>) {
      return {
        ...state,
        headerColumnsInit: action.payload,
      }
    },

    setConvertedInfo(state, action: PayloadAction<Types.IConvertedInfo>) {
      return {
        ...state,
        convertedInfo: Object.assign({ ...state.convertedInfo }, action.payload),
      }
    },
    /** 重置一下数据 */
    resetConvertedInfo(state, actions: PayloadAction<boolean>) {
      if (actions.payload) {
        return {
          ...state,
          convertedInfo: { ...defaultConvertedInfo },
        }
      } else {
        // 如果已经切换过商品币种，重置会导致store跟表单值无法对应，除非全部重新设置
        return {
          ...state,
          convertedInfo: {
            ...defaultConvertedInfo,
            goodsCurrency: state.convertedInfo.goodsCurrency,
          },
        }
      }
    },
    setSearchConditions(state, action: PayloadAction<Types.SearchConditions>) {
      return {
        ...state,
        searchConditions: action.payload,
      }
    },
    setActiveKey(state, action: PayloadAction<ORDER_STATUS_MAP>) {
      return {
        ...state,
        activeKey: action.payload,
      }
    },
    updateActiveCondition(state, action: PayloadAction<Types.ActiveCondition>) {
      return {
        ...state,
        activeCondition: {
          ...(state.activeCondition || {}),
          ...action.payload,
        },
      }
    },
    /** 获取标签数据 */
    setCustomTags(state, action: PayloadAction<Types.CustomTag[]>) {
      return {
        ...state,
        customTags: action.payload,
      }
    },
  },
})

export const {
  setExpress,
  setStocks,
  setCurrentPage,
  setHeaderColumns,
  setHeaderColumnsInit,
  setConvertedInfo,
  resetConvertedInfo,
  setSearchConditions,
  setActiveKey,
  updateActiveCondition,
  setCustomTags,
} = orderSlice.actions

/** 查询库存信息 */
export const getStocks = (params: Types.IBatchStockParams) => async (dispatch: AppDispatch) => {
  const resp = await http.admin.post(stockUrl.stockNums, {
    warehouseId: params?.warehouseId,
    skuIds: params.skuIds,
    skuGroupIds: params.skuGroupIds,
  })

  dispatch(setStocks(resp.data?.list))
}

export const updateSearchCondition = (dataValue: any) => async (dispatch: AppDispatch) => {
  await updateUserSetting({
    settingCode: SettingCode.outbound_search_shortcut,
    dataValue,
  })
  dispatch(setSearchConditions(dataValue))
}

export const getSearchCondition = () => async (dispatch: AppDispatch) => {
  const res = await getUserSetting({ settingCode: SettingCode.outbound_search_shortcut })
  dispatch(setSearchConditions(res.data.dataValue as Types.SearchConditions))
}

/** 获取标签数据 */
export const getCustomTagsAction = () => async (dispatch: AppDispatch) => {
  const { data } = await getCustomTagsList()
  dispatch(setCustomTags(data.tags))
  return data
}

export default orderSlice.reducer
